import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, renderList as _renderList, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-219b2eac"),n=n(),_popScopeId(),n)
const _hoisted_1 = { id: "huddles-container" }
const _hoisted_2 = { class: "column column--left" }
const _hoisted_3 = { class: "base-display" }
const _hoisted_4 = ["src"]
const _hoisted_5 = {
  key: 2,
  class: "base-display"
}
const _hoisted_6 = { class: "column column--right" }
const _hoisted_7 = {
  key: 0,
  class: "huddle-empty-text"
}
const _hoisted_8 = ["onClick"]
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("i", { class: "pi pi-times" }, null, -1))
const _hoisted_10 = [
  _hoisted_9
]
const _hoisted_11 = {
  key: 0,
  id: "huddles-error-container"
}

import { ref, computed, onMounted, type ComputedRef, type Ref } from 'vue'
import { useStore } from 'vuex'
import { pendoTrackEvent } from '@/utils/pendo'
import { getCDNUrl } from '@/utils/cdn'
import { useToast } from 'primevue/usetoast'
import { Permissions } from '@/mobile/src/types/auth'
import { fetchRosters, fetchBaseInfo } from '@/api/huddles'
import {
    Huddle,
    HuddlesRoster,
    BaseGoals,
    HuddlesBaseInfo,
    CustomerHighlight,
} from '@/entities/huddles'
import Carousel from 'primevue/carousel'
import Toast from 'primevue/toast'
import Dialog from 'primevue/dialog'
import HuddlesLoading from '@/mobile/src/views/appV4/huddles/HuddlesLoading.vue'
import HuddlesError from '@/mobile/src/views/appV4/huddles/HuddlesError.vue'
import HuddlesPromotion from '@/mobile/src/views/appV4/huddles/HuddlesPromotion.vue'
import HuddleDetails from '@/mobile/src/views/appV4/huddles/HuddleDetails.vue'
import HuddlesCustomerHighlights from '@/mobile/src/views/appV4/huddles/HuddlesCustomerHighlights.vue'
import HuddlesOverviewStats from '@/mobile/src/views/appV4/huddles/HuddlesOverviewStats.vue'
import HuddlesDatePaginator from '@/mobile/src/views/appV4/huddles/HuddlesDatePaginator.vue'
import HuddlesRevenueGoal from '@/mobile/src/views/appV4/huddles/HuddlesRevenueGoal.vue'
import PullToRefreshWrapper from '@/mobile/src/components/appV4/PullToRefreshWrapper.vue'
import moment from 'moment'


export default /*#__PURE__*/_defineComponent({
  __name: 'Huddles',
  setup(__props) {

const store = useStore()
const toast = useToast()

const loadingEntirePage = ref(true)
const loadingHuddleList = ref(false)
const refreshing = ref(false)
const showError = ref(false)
const selectedHuddleId = ref()
const showHuddleModal = ref(false)
const selectedDate = ref('')
const huddlesRosters: Ref<HuddlesRoster[]> = ref([])
const promotionMessage = ref('')
const base: Ref<string> = ref('')
const baseGoals: Ref<BaseGoals> = ref({
    cxscore: undefined,
    cxscore_goal: undefined,
    cxscore_goal_range: undefined,
    last_month_sales_per_head: 0,
    sales_per_head_goal: 0,
    last_month: '',
})
const customerHighlights: Ref<CustomerHighlight[]> = ref([])

onMounted(() => {
    fetchHuddlesData()
})

const fetchHuddlesData = async () => {
    loadingEntirePage.value = true
    await fetchBaseData()
    await fetchRosterData()
    loadingEntirePage.value = false
}

const hasNetworkConnection = computed(
    () => store.getters['hasNetworkConnection']
)

const pullToRefresh = async () => {
    if (hasNetworkConnection.value) {
        refreshing.value = true
        await fetchHuddlesData()
    }
    refreshing.value = false
}

const fetchBaseData = async () => {
    await fetchBaseInfo()
        .then((response) => {
            const baseInfo: HuddlesBaseInfo = response.data.result
            promotionMessage.value = baseInfo.promotion ?? undefined
            baseGoals.value = baseInfo.goals
            customerHighlights.value = baseInfo.customer_highlights ?? []
            base.value = baseInfo.base ?? ''
        })
        .catch(() => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Error fetching base info',
                life: 2000,
            })
            showError.value = true
        })
}
const companyLogo: ComputedRef<string> = computed(() => {
    if (store.getters['details'].company?.logo) {
        return getCDNUrl(store.getters['details'].company?.logo)
    }
    return ''
})

const isAdmin: ComputedRef<boolean> = computed(() =>
    store.getters['permissions']?.includes(Permissions.RoleAdminBasics)
)
const fetchRosterData = async () => {
    await fetchRosters()
        .then((response) => {
            huddlesRosters.value = response.data.result.rosters
            selectedDate.value = moment(new Date()).format().split('T')[0]
        })
        .catch(() => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Error loading rosters',
                life: 2000,
            })
            showError.value = true
        })
}

const openHuddleModal = (huddleId: number) => {
    selectedHuddleId.value = huddleId
    showHuddleModal.value = true
}

const selectedDatesHuddles: ComputedRef<Huddle[]> = computed(() => {
    return (
        huddlesRosters.value.find((huddle) => {
            return huddle.date === selectedDate.value
        })?.huddles ?? []
    )
})

const getSelectedHuddle: ComputedRef<Huddle | undefined> = computed(() => {
    return selectedDatesHuddles.value.find(
        (huddle) => huddle.id === selectedHuddleId.value
    )
})

const onPromotionUpdated = async () => {
    loadingEntirePage.value = true
    await fetchBaseData()
    loadingEntirePage.value = false
}

const getCarouselDetails = computed(() => {
    const selectedHuddleDetailindex = selectedDatesHuddles.value.findIndex(
        (huddle) => huddle.id === selectedHuddleId.value
    )

    if (selectedHuddleDetailindex < 1) {
        return selectedDatesHuddles.value
    }

    const beforeIndex = selectedDatesHuddles.value.slice(
        0,
        selectedHuddleDetailindex
    )
    const afterIndex = selectedDatesHuddles.value.slice(
        selectedHuddleDetailindex
    )

    return [...afterIndex, ...beforeIndex]
})

const canPaginate = (direction: 'previous' | 'next'): boolean => {
    const nextDay = new Date(selectedDate.value)
    nextDay.setDate(nextDay.getDate() - (direction === 'previous' ? 1 : -1))

    const nextDayHuddle = huddlesRosters.value.find((huddle) => {
        return huddle.date === moment(nextDay).format().split('T')[0]
    })
    return !!nextDayHuddle
}

const onPaginationChange = (direction: 'previous' | 'next') => {
    pendoTrackEvent(`huddles-paginate-to-${direction}-day`)

    const nextDay = new Date(selectedDate.value)
    nextDay.setDate(nextDay.getDate() - (direction === 'previous' ? 1 : -1))
    selectedDate.value = moment(nextDay).format().split('T')[0]
}

const getFlightKey = (huddle: Huddle): string => {
    return huddle.flight_number + huddle.depart_station + huddle.arrival_station
}

const revenueTotalTarget = computed(() => {
    if (!selectedDatesHuddles.value.length) {
        return 'N/A'
    }

    let revenueTotalGoal = 0

    selectedDatesHuddles.value.forEach((flight) => {
        revenueTotalGoal += flight.revenue_goal || 0
    })

    return '$' + revenueTotalGoal.toFixed(0)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(PullToRefreshWrapper, {
    ref: "refreshContainerHuddles",
    class: "page-wrapper",
    disable: !hasNetworkConnection.value,
    "ptr-refreshing": refreshing.value,
    "refresh-container-classes": "page-wrapper",
    onRefreshAction: pullToRefresh
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_unref(Toast)),
        _createElementVNode("div", _hoisted_2, [
          (loadingEntirePage.value)
            ? (_openBlock(), _createBlock(HuddlesLoading, {
                key: 0,
                type: "left-column"
              }))
            : (!showError.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createElementVNode("div", _hoisted_3, _toDisplayString(base.value), 1),
                  _createElementVNode("img", {
                    id: "company-logo",
                    src: companyLogo.value,
                    alt: "logo"
                  }, null, 8, _hoisted_4),
                  _createVNode(HuddlesOverviewStats, {
                    "base-goals": baseGoals.value,
                    "cxscore-as-percentage": true
                  }, null, 8, ["base-goals"]),
                  (
                            isAdmin.value ||
                            (promotionMessage.value && promotionMessage.value.length)
                        )
                    ? (_openBlock(), _createBlock(HuddlesPromotion, {
                        key: 0,
                        "promotion-message": promotionMessage.value,
                        onPromotionUpdated: onPromotionUpdated
                      }, null, 8, ["promotion-message"]))
                    : _createCommentVNode("", true),
                  _createVNode(HuddlesCustomerHighlights, { highlights: customerHighlights.value }, null, 8, ["highlights"])
                ], 64))
              : (_openBlock(), _createElementBlock("div", _hoisted_5, "-"))
        ]),
        _createElementVNode("div", _hoisted_6, [
          (loadingEntirePage.value)
            ? (_openBlock(), _createBlock(HuddlesLoading, {
                key: 0,
                type: "date-paginator"
              }))
            : (_openBlock(), _createBlock(HuddlesDatePaginator, {
                key: 1,
                loading: loadingHuddleList.value,
                "has-next": canPaginate('next'),
                "has-prev": canPaginate('previous'),
                "selected-date": selectedDate.value,
                onOnPaginate: onPaginationChange
              }, null, 8, ["loading", "has-next", "has-prev", "selected-date"])),
          (loadingEntirePage.value || loadingHuddleList.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, _renderList(3, (card) => {
                return _createVNode(HuddlesLoading, {
                  key: card,
                  type: "huddle-card"
                })
              }), 64))
            : (!showError.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                  (!selectedDatesHuddles.value.length)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_7, " No roster data available "))
                    : _createCommentVNode("", true),
                  (selectedDatesHuddles.value.length)
                    ? (_openBlock(), _createBlock(HuddlesRevenueGoal, {
                        key: 1,
                        "revenue-total-target": revenueTotalTarget.value
                      }, null, 8, ["revenue-total-target"]))
                    : _createCommentVNode("", true),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedDatesHuddles.value, (huddle) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: getFlightKey(huddle),
                      class: "huddles-card",
                      onClick: ($event: any) => (openHuddleModal(huddle.id))
                    }, [
                      _createVNode(HuddleDetails, {
                        huddle: huddle,
                        "base-cxscore-goal": baseGoals.value.cxscore_goal,
                        "summary-view": true
                      }, null, 8, ["huddle", "base-cxscore-goal"])
                    ], 8, _hoisted_8))
                  }), 128)),
                  _createVNode(_unref(Dialog), {
                    visible: showHuddleModal.value,
                    "onUpdate:visible": _cache[1] || (_cache[1] = ($event: any) => ((showHuddleModal).value = $event)),
                    class: "huddle-details-modal",
                    modal: ""
                  }, {
                    container: _withCtx(() => [
                      _createElementVNode("button", {
                        class: "close-modal-button",
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (showHuddleModal.value = false))
                      }, _hoisted_10),
                      _createVNode(_unref(Carousel), {
                        value: getCarouselDetails.value,
                        "num-visible": 1,
                        "num-scroll": 1,
                        "show-navigators": false,
                        circular: ""
                      }, {
                        item: _withCtx((slotProps) => [
                          (getSelectedHuddle.value)
                            ? (_openBlock(), _createBlock(HuddleDetails, {
                                key: 0,
                                huddle: slotProps.data,
                                "base-cxscore-goal": 
                                            baseGoals.value.cxscore_goal
                                        
                              }, null, 8, ["huddle", "base-cxscore-goal"]))
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      }, 8, ["value"])
                    ]),
                    _: 1
                  }, 8, ["visible"])
                ], 64))
              : _createCommentVNode("", true)
        ])
      ]),
      (showError.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
            _createVNode(HuddlesError, { type: "page" })
          ]))
        : _createCommentVNode("", true)
    ], undefined, true),
    _: 1
  }, 8, ["disable", "ptr-refreshing"]))
}
}

})